/**
 * `<UploadTrayPanel />` — the tray body shown inside the floating toast.
 *
 * Renamed from `UploadTrayPopover` once the sidebar surface was retired:
 * the body is no longer mounted in a Radix `Popover`, it lives inside
 * the `<UploadToast />` floating panel (collapsed/expanded state owned
 * by the toast, not by this component). The render contract is
 * IDENTICAL to the pre-rename version — only the name changed, plus
 * this JSDoc.
 *
 * Layout:
 *   - Header: "Uploads" + total count
 *   - Body: items grouped by source (Wizard/Folder/Library) with section
 *     header counts; within each group, active items first then terminal
 *   - Footer: "Clear completed" — disabled when no terminal items
 *
 * Never auto-opens / never auto-closes — visibility is entirely driven
 * by the parent toast's expand/collapse state, which is in turn user-
 * driven (click on the toast header).
 *
 * This tray subscribes ONLY to ID arrays (one per source, sorted
 * active-first by the selector) plus the counts object. Subscribing to
 * `useUploadQueueStore((s) => s.uploads)` instead would mean every axios
 * progress tick (60Hz) rewrites the uploads ref and re-renders the whole
 * tray. With per-source ID subscriptions, the tray
 * re-renders ONLY on membership / status changes — never on progress
 * ticks. Individual rows still update because each `<UploadItemRow />`
 * subscribes per-item via `useUploadById(id)`.
 *
 * `counts.cancelled` is consumed below to compute `hasTerminal` without
 * touching the uploads map directly — keeping the no-full-subscription
 * invariant.
 */
import { Button } from '@monksflow/monks-ui';
import { useUploadCounts, useUploadsBySource } from '../../model/selectors';
import { useUploadQueueStore } from '../../model/upload-queue.store';
import type { UploadSource } from '../../model/upload-queue.store.types';
import { UploadItemRow } from '../UploadItemRow';
import { useTranslation } from 'react-i18next';

const SOURCE_ORDER = [
  { key: 'wizard', labelKey: 'uploadQueue.uploadTrayPanel.wizard' },
  { key: 'folder', labelKey: 'uploadQueue.uploadTrayPanel.folder' },
  { key: 'library', labelKey: 'uploadQueue.uploadTrayPanel.library' },
] as const satisfies ReadonlyArray<{ key: UploadSource; labelKey: string }>;

export function UploadTrayPanel() {
  const { t } = useTranslation('projects');

  const counts = useUploadCounts();
  const wizardIds = useUploadsBySource('wizard');
  const folderIds = useUploadsBySource('folder');
  const libraryIds = useUploadsBySource('library');

  // hasTerminal == any terminal (success | failed | cancelled) is in the
  // queue. Derive from counts so the tray never reads the full uploads
  // map — that subscription would re-render the tray on every progress
  // tick and undo the per-source ID subscriptions above.
  const hasTerminal = counts.success + counts.failed + counts.cancelled > 0;

  if (counts.total === 0) {
    return (
      <div className="w-80 p-4 text-sm text-muted-foreground" data-testid="upload-tray-empty">
        {t('uploadQueue.uploadTrayPanel.noActiveUploads')}
      </div>
    );
  }

  const idsBySource: Record<UploadSource, string[]> = {
    wizard: wizardIds,
    folder: folderIds,
    library: libraryIds,
  };

  return (
    <div className="w-80" data-testid="upload-tray">
      <div className="flex items-center justify-between border-b px-3 py-2">
        <span className="text-sm font-semibold">{t('uploadQueue.uploadTrayPanel.uploads')}</span>
        <span className="text-xs text-muted-foreground" data-testid="upload-tray-header-count">
          {counts.total}
        </span>
      </div>

      <div className="max-h-96 overflow-y-auto py-1">
        {SOURCE_ORDER.map(({ key, labelKey }) => {
          const ids = idsBySource[key];
          if (ids.length === 0) return null;
          return (
            <div key={key} className="py-1">
              <div className="flex items-center justify-between px-3 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <span>{t(labelKey)}</span>
                <span data-testid={`upload-tray-group-count-${key}`}>{ids.length}</span>
              </div>
              {ids.map((id) => (
                <UploadItemRow key={id} id={id} />
              ))}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end border-t px-3 py-2">
        <Button
          variant="ghost"
          size="mini"
          disabled={!hasTerminal}
          onClick={() => useUploadQueueStore.getState().dismissAllTerminal()}
        >
          {t('uploadQueue.uploadTrayPanel.clearCompleted')}
        </Button>
      </div>
    </div>
  );
}
